import { jsx as _jsx } from "react/jsx-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, } from 'react';
import { sdkEvents } from '../core/events.js';
import { installMarketConfigRefresh, marketConfigCatalog, requestMarketConfig, } from '../core/internal/market-config-catalog.js';
import { useGetMarketConfigs } from '../generated/hooks/useGetMarketConfigs.gen.js';
import { PersistenceRestoredContext } from './PersistenceRestoredContext.js';
const CatalogContext = createContext(null);
/** The only catalog QueryObserver in an SDK/network tree. Leaves read this context. */
export function MarketConfigProvider({ children }) {
    const restored = useContext(PersistenceRestoredContext);
    const client = useQueryClient();
    const result = useGetMarketConfigs({}, { enabled: restored, retry: false });
    const refetch = result.refetch;
    // Register before leaf passive effects request an unknown restored address.
    useLayoutEffect(() => {
        if (!restored)
            return;
        return installMarketConfigRefresh(client, async () => {
            const refreshed = await refetch({ cancelRefetch: false });
            if (refreshed.isError) {
                sdkEvents.emit('cache:diagnostic', { kind: 'market-config', reason: 'refresh-failed' });
                throw refreshed.error ?? new Error('market config refresh failed');
            }
        });
    }, [client, refetch, restored]);
    const retry = useCallback((address = '*') => requestMarketConfig(client, address, true), [client]);
    const ensure = useCallback((address) => {
        if (restored)
            requestMarketConfig(client, address);
    }, [client, restored]);
    const refresh = useCallback(async () => {
        if (restored)
            await refetch({ cancelRefetch: false });
    }, [refetch, restored]);
    const configs = marketConfigCatalog(result.data);
    const status = !restored
        ? 'loading'
        : configs !== undefined
            ? 'ready'
            : result.isError || result.data !== undefined
                ? 'unavailable'
                : 'loading';
    const { error, isFetching, dataUpdatedAt, errorUpdatedAt, failureCount } = result;
    const value = useMemo(() => ({
        configs,
        status,
        retry,
        ensure,
        refetch: refresh,
        error,
        isFetching,
        dataUpdatedAt,
        errorUpdatedAt,
        failureCount,
    }), [
        configs,
        status,
        retry,
        ensure,
        refresh,
        error,
        isFetching,
        dataUpdatedAt,
        errorUpdatedAt,
        failureCount,
    ]);
    return _jsx(CatalogContext.Provider, { value: value, children: children });
}
export function useMarketConfigCatalog() {
    const value = useContext(CatalogContext);
    if (value === null)
        throw new Error('MarketConfigProvider is required');
    return value;
}
export function useMarketConfig(address) {
    const catalog = useContext(CatalogContext);
    if (address != null && catalog === null)
        throw new Error('MarketConfigProvider is required');
    const client = useQueryClient();
    const config = address == null ? undefined : catalog?.configs?.[address];
    const status = catalog?.status;
    useEffect(() => {
        if (status === 'ready' && config === undefined && address != null)
            requestMarketConfig(client, address);
    }, [address, client, config, status]);
    return config;
}
//# sourceMappingURL=MarketConfigProvider.js.map